import { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { AssigneePicker } from './AssigneePicker'
import { type Assignee, type Status } from '../lib/types'

type Props = {
  status: Status
  /** Last person picked anywhere on the board — the sensible default. */
  defaultAssignee: Assignee
  onAdd: (input: { title: string; assignee: Assignee; status: Status }) => void
}

export function Composer({ status, defaultAssignee, onAdd }: Props) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [assignee, setAssignee] = useState<Assignee>(defaultAssignee)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Follow the board default until the composer is opened and changed by hand.
  useEffect(() => {
    if (!open) setAssignee(defaultAssignee)
  }, [defaultAssignee, open])

  const submit = () => {
    const trimmed = title.trim()
    if (!trimmed) return
    onAdd({ title: trimmed, assignee, status })
    setTitle('')
    inputRef.current?.focus()
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true)
          requestAnimationFrame(() => inputRef.current?.focus())
        }}
        className="flex w-full items-center gap-1.5 rounded-xl border border-dashed border-cream-200
                   px-3 py-2.5 text-sm font-medium text-muted transition
                   hover:border-teal-300 hover:bg-white hover:text-teal"
      >
        <Plus className="size-4" />
        Add task
      </button>
    )
  }

  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-teal-300">
      <textarea
        ref={inputRef}
        rows={2}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
          if (e.key === 'Escape') {
            setTitle('')
            setOpen(false)
          }
        }}
        placeholder="What needs doing?"
        aria-label="Card title"
        className="w-full resize-none text-sm leading-snug text-ink placeholder:text-faint focus:outline-none"
      />

      <div className="mt-2 flex items-center justify-between gap-2">
        <AssigneePicker value={assignee} onChange={setAssignee} />
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              setTitle('')
              setOpen(false)
            }}
            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted hover:bg-cream-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!title.trim()}
            className="rounded-lg bg-teal px-3 py-1.5 text-xs font-semibold text-white transition
                       hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  )
}
